"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.BbplGraph = void 0;
const _ = __importStar(require("lodash"));
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
var BbplGraph;
(function (BbplGraph) {
    const anyConfigType = (_x) => true;
    /**
     * Note: in almost all cases we don't care about the config type, and `anyConfigType` is the
     * appropriate typeguard. Unnecessary typeguards can hurt performance, especially if called in a
     * tight loop.
     */
    const findAtMostOneTypedArtifact = (artifacts, type, configGuard, identifiers) => {
        const filtered = artifacts.filter((artifact) => artifact.type === type);
        (0, common_1.finleyAssert)(filtered.length <= 1, `${identifiers} expected at most one ${type}, got [${filtered.length}]`);
        if (!filtered.length) {
            return null;
        }
        const { id, configuration, ...otherFields } = filtered[0];
        (0, common_1.finleyAssert)(configGuard(configuration), `${identifiers} ${type} [artifactId: ${id}] does not satisfy typeguard`);
        return { id, configuration, ...otherFields };
    };
    /**
     * Note: in almost all cases we don't care about the config type, and `anyConfigType` is the
     * appropriate typeguard. Unnecessary typeguards can hurt performance, especially if called in a
     * tight loop.
     */
    const findOneTypedArtifact = (artifacts, type, configGuard, identifiers) => {
        const artifact = findAtMostOneTypedArtifact(artifacts, type, configGuard, identifiers);
        (0, common_1.finleyAssert)(artifact, `${identifiers} expected one ${type}, got none`);
        return artifact;
    };
    const getGraphType = (graph) => {
        const artifactTypes = new Set(graph.artifacts.map(({ type }) => type));
        // There are two disjoint graphs in funding verification - one to parse an xlsx report, extract
        // loan/supplemental/cash tapes from it, generate a borrowing base report, and then a
        // verification report. And another that records an approval/rejection of the verification,
        // and creates the associated funding request.
        if (artifactTypes.has('fundingVerificationReport') ||
            artifactTypes.has('fundingApprovalReport')) {
            return 'fundingVerification';
        }
        // temp: this code is going away
        // asset verification and confgTape are  disjoint graphs that this code need not to worry about.
        // TODO(jon): this code may live a bit longer. We should identify borrower graphs by positive
        //  match rather than by process of elimination. (This is brittle because it will break every
        //  time we add a new graph type.)
        if (artifactTypes.has('rawReceivableDocumentation') ||
            artifactTypes.has('receivableInvoice') ||
            artifactTypes.has('configTape')) {
            return null;
        }
        // In most cases, a forwarded tape graph has only two nodes - a raw node for ingestion and
        // a forwarded tape node to forward the tape to SFTP. These should be hidden from manual
        // uploader by returning null here.
        //
        // In rare cases, we attach a forwarded tape node to a graph that has other purposes. (We
        // detect this case by the presence of a `generated` node.) We would not want to exclude those
        // graphs from being shown in uploader.
        if (artifactTypes.has('forwardedTape') &&
            finley_types_1.DbTable.GeneratedArtifactTypeIterable.every((generatedType) => !artifactTypes.has(generatedType))) {
            return null;
        }
        // Graphs that contain `orgProvidedInput` terminal node, will aggregate some value from a csv and write
        // the aggregated primitive value for the provided org provided key into the `org_provided_values` table.
        // Similarly, graphs that contain `generatedFinancialData` terminal node, will read from a csv upload and write
        // to the `financial_statement_values` table.
        if (artifactTypes.has('orgProvidedInput') ||
            artifactTypes.has('orgProvidedInputs') ||
            artifactTypes.has('generatedFinancialData')) {
            return 'orgProvidedInput';
        }
        // There are several disjoint graph types for borrower funding requests.
        //
        // 1) accepts loan and supplemental tapes, runs calc agent, generates a receivable response tape
        //    preview
        // 2) accepts the user's assignment decisions upon a receivable response tape preview and
        //    generates a borrowing base report and possibly a funding request
        // 3) a combination of (1) and (2), for non-assignment borrowers.
        // 4) accepts a raw cash balance feed and generates a manual bank account report.
        //
        // These aren't quite as easy to distinguish as asset verification and funding verification
        // graphs. Instead, once we've ruled those two possibilities out, we sipmly assume we're looking
        // at a borrower funding request graph.
        return 'borrowerFundingRequest';
    };
    /** Divides an ArtifactDocument into multiple disjoint graphs. */
    const getDisjointGraphs = (artifactDocument) => {
        // Create a single element array for each artifact. Prior to considering any graph edges, we
        // treat each of these as its own disjoint group.
        let artifactIdGroups = artifactDocument.artifacts.map(({ id }) => [id]);
        artifactDocument.dependencies.forEach(({ inputArtifactId, outputArtifactId }) => {
            // Partition into groups that include these artifact ids and groups that don't.
            const [affectedGroups, unaffectedGroups] = _.partition(artifactIdGroups, (group) => group.includes(inputArtifactId) || group.includes(outputArtifactId));
            (0, common_1.finleyAssert)(affectedGroups.length === 1 || affectedGroups.length === 2, `for [artifactDocumentId: ${artifactDocument.id}] dependency [inputArtifactId: ${inputArtifactId}], [outputArtifactId: ${outputArtifactId}] should overlap either 1 or 2 groups`);
            // Take all affected groups and merge them into a single group, since the dependency record
            // has now told us these groups are connected.
            const newlyCombinedGroup = _.uniq([
                ..._.flatten(affectedGroups),
                inputArtifactId,
                outputArtifactId,
            ]);
            // Update the list of all groups.
            artifactIdGroups = [...unaffectedGroups, newlyCombinedGroup];
        });
        return artifactIdGroups.map((group) => ({
            artifactDocumentId: artifactDocument.id,
            artifacts: artifactDocument.artifacts.filter(({ id }) => group.includes(id)),
            dependencies: artifactDocument.dependencies.filter(({ inputArtifactId }) => group.includes(inputArtifactId)),
        }));
    };
    const mergeGraphs = (graphs) => {
        const types = new Set(graphs.map(({ type }) => type));
        const docIds = new Set(graphs.map(({ artifactDocumentId }) => artifactDocumentId));
        (0, common_1.finleyAssert)(types.size === 1, 'graph types do not match');
        (0, common_1.finleyAssert)(docIds.size === 1, `artifact document ids do not match: ${JSON.stringify([...docIds])}`);
        const [type] = types;
        const [artifactDocumentId] = docIds;
        return {
            artifactDocumentId,
            type,
            artifacts: graphs.flatMap(({ artifacts }) => artifacts),
            dependencies: graphs.flatMap(({ dependencies }) => dependencies),
            disjointGraphs: graphs.flatMap(({ disjointGraphs }) => disjointGraphs),
            loanProductSlugs: _.chain(graphs)
                .flatMap((graph) => graph.loanProductSlugs)
                .uniq()
                .value(),
            creditFacilitySlugs: _.chain(graphs)
                .flatMap((graph) => graph.creditFacilitySlugs)
                .uniq()
                .value(),
        };
    };
    /**
     * Disjoint graphs of type 'fundingVerification' should each be scoped to a single creditFacilityId.
     * fundingVerification graphs are only 'related' if they service the same credit facility.
     * A single artifact document for a borrower could contain multiple funding verification graphs if they have multiple facilities,
     * so we want to make sure to only join ones with the same facility.
     * Disjoint fundingVerification graphs may not always carry loanProduct in their nodes,
     * For example, the FundingApprovalReport -> FundingRequest disjoint graph will only carry the creditFacilityId,
     * whereas the main xlsx2csv -> verify graph will carry all relevant loan products in addition to the creditFacilityId.
     * Therefore we will not use loan products to determine the relatedness of funding verification graphs
     * @returns whether two graphs are both funding verification graph types and are related
     */
    const areFundingVerificationGraphsRelated = ({ type: typeA, creditFacilitySlugs: cfA }, { type: typeB, creditFacilitySlugs: cfB }) => typeA === 'fundingVerification' &&
        typeB === 'fundingVerification' &&
        common_1.utils.arraysContainSameValues({ array1: cfA, array2: cfB });
    const areGraphsRelated = (graphA, graphB) => {
        const { type: typeA, loanProductSlugs: lpA, creditFacilitySlugs: cfA, } = graphA;
        const { type: typeB, loanProductSlugs: lpB, creditFacilitySlugs: cfB, } = graphB;
        const areFundingVerificationGraphs = typeA === 'fundingVerification' && typeB === 'fundingVerification';
        if (areFundingVerificationGraphs) {
            return areFundingVerificationGraphsRelated(graphA, graphB);
        }
        return (typeA === typeB &&
            (_.intersection(lpA, lpB).length > 0 ||
                _.intersection(cfA, cfB).length > 0));
    };
    const toConcreteGraph = (graph) => {
        switch (graph.type) {
            case 'fundingVerification': {
                const { loanProductSlugs, creditFacilitySlugs } = graph;
                (0, common_1.finleyAssert)(loanProductSlugs.length >= 1 && creditFacilitySlugs.length === 1, `Lender verification graph has loan products ${JSON.stringify(loanProductSlugs)} and credit facilities ${JSON.stringify(creditFacilitySlugs)}; expected one or more loan products and exactly one credit facility.`);
                const [creditFacilitySlug] = creditFacilitySlugs;
                const { artifactDocumentId, disjointGraphs, artifacts, dependencies, } = graph;
                const findOne = (type) => findOneTypedArtifact(artifacts, type, anyConfigType, `fundingVerification [creditFacilitySlug: ${creditFacilitySlug}]`);
                return {
                    artifactDocumentId,
                    type: 'fundingVerification',
                    disjointGraphs,
                    artifacts,
                    dependencies,
                    loanProductSlugs,
                    creditFacilitySlugs,
                    creditFacilitySlug,
                    // A verification graph has one raw loan tape - the servicer report.
                    servicerReportArtifact: () => findOne('rawLoanTape'),
                    // A verification graph has one supplemental tape, for waivers. It has a single field
                    // called `receivable_id`. Presence of a receivable id indicates the lender has waived
                    // eligibility for that receivable.
                    waiverTapeArtifact: () => findOne('rawSupplementalTape'),
                    receivableResponseTapeArtifact: () => findOne('receivableResponseTape'),
                    borrowingBaseReportArtifact: () => findOne('borrowingBaseReport'),
                    verificationReportArtifact: () => findOne('fundingVerificationReport'),
                    approvalReportArtifact: () => findOne('fundingApprovalReport'),
                    fundingRequestArtifact: () => findOne('fundingRequest'),
                };
            }
            case 'orgProvidedInput': {
                const { type } = graph;
                const getRelatedCreditFacilitySlugs = (_artifact) => {
                    // Org provided graphs encompass and entire borrower and is assumed to be associated with all facilities in the graph.
                    return graph.creditFacilitySlugs;
                };
                return { ...graph, type, getRelatedCreditFacilitySlugs };
            }
            case 'borrowerFundingRequest': {
                // TODO(jon): add validations.
                const { type, artifacts } = graph;
                const getRelatedCreditFacilitySlugs = (artifact) => {
                    // If the artifact has a creditFacilitySlug, it is associated with only that facility.
                    if (artifact.creditFacilitySlug)
                        return [artifact.creditFacilitySlug];
                    // There exists exactly one assignedLoanTape for every (lp, cf) pair.
                    // If the artifact has a loanProductSlug, look up via assignedLoanTapes.
                    if (artifact.loanProductSlug)
                        return _.chain(artifacts)
                            .filter(({ type: t }) => t === 'assignedLoanTape')
                            .flatMap(({ id, loanProductSlug, creditFacilitySlug }) => {
                            (0, common_1.finleyAssert)(loanProductSlug && creditFacilitySlug, `assignedLoanTape [artifactId: ${id}] should have loan product and credit facility slug`);
                            return loanProductSlug === artifact.loanProductSlug
                                ? [creditFacilitySlug]
                                : [];
                        })
                            .uniq()
                            .value();
                    // Otherwise, the artifact is assumed to be associated with all facilities in the graph.
                    return graph.creditFacilitySlugs;
                };
                return { ...graph, type, getRelatedCreditFacilitySlugs };
            }
            default:
                throw (0, common_1.finleyAssertUnreachable)(graph.type);
        }
    };
    /**
     * This module is only intended to be used by the artifact module which will
     *
     * - Fetch artifact document
     * - Parse it using this library
     * - Do auth using information provided by this library
     * - Surface the {@link Graph} via the artifact module API.
     *
     * All other code should ideally access the artifact document via the artifact module.
     */
    let PrivateForArtifactModule;
    (function (PrivateForArtifactModule) {
        PrivateForArtifactModule.getGraphs = (artifactDocument, graphFilter = () => true, shouldMergeGraphs = areGraphsRelated) => {
            const disjointGraphs = getDisjointGraphs(artifactDocument);
            const graphs = disjointGraphs
                .map((disjointGraph) => {
                const loanProductSlugs = new Set();
                const creditFacilitySlugs = new Set();
                disjointGraph.artifacts.forEach(({ loanProductSlug, creditFacilitySlug }) => {
                    if (loanProductSlug)
                        loanProductSlugs.add(loanProductSlug);
                    if (creditFacilitySlug)
                        creditFacilitySlugs.add(creditFacilitySlug);
                });
                const type = getGraphType(disjointGraph);
                if (type === null) {
                    return null;
                }
                if (type === 'orgProvidedInput') {
                    // For org provided inputs, we only have organization id.
                    // This means that `creditFacilitySlugs` will be empty.
                    // However, a borrower who configured this graph has authorized
                    // this behavior across all of their credit facilities.
                    // Therefore, we pass in all credit facility ids here.
                    for (const cfSlug of artifactDocument.cfIdToSlugMap.values()) {
                        creditFacilitySlugs.add(cfSlug);
                    }
                }
                return {
                    type,
                    artifactDocumentId: disjointGraph.artifactDocumentId,
                    artifacts: disjointGraph.artifacts,
                    dependencies: disjointGraph.dependencies,
                    disjointGraphs: [disjointGraph],
                    loanProductSlugs: [...loanProductSlugs],
                    creditFacilitySlugs: [...creditFacilitySlugs],
                };
            })
                .filter(common_1.typeguards.isNotNullOrUndefined);
            let mergedGraphs = [];
            graphs.forEach((graphToConsider) => {
                const [relatedGraphs, unrelatedGraphs] = _.partition(mergedGraphs, (graph) => shouldMergeGraphs(graph, graphToConsider));
                const mergedGraph = mergeGraphs([...relatedGraphs, graphToConsider]);
                mergedGraphs = [...unrelatedGraphs, mergedGraph];
            });
            return mergedGraphs.filter(graphFilter).map(toConcreteGraph);
        };
        PrivateForArtifactModule.getFundingVerificationGraph = ({ artifactDocument, creditFacilitySlug, }) => {
            const graphs = [];
            PrivateForArtifactModule.getGraphs(artifactDocument).forEach((graph) => {
                if (graph.type === 'fundingVerification' &&
                    graph.creditFacilitySlug === creditFacilitySlug)
                    graphs.push(graph);
            });
            (0, common_1.finleyAssert)(graphs.length <= 1, `found ${graphs.length} [creditFacilitySlug: ${creditFacilitySlug}] funding verification graphs in [artifactDocumentId: ${artifactDocument.id}] `);
            return graphs[0] ?? null;
        };
        PrivateForArtifactModule.getBorrowerFundingRequestAndOrgProvidedInputGraphs = ({ artifactDocument, }) => {
            const graphs = [];
            PrivateForArtifactModule.getGraphs(artifactDocument).forEach((graph) => {
                if (graph.type === 'borrowerFundingRequest' ||
                    graph.type === 'orgProvidedInput')
                    graphs.push(graph);
            });
            return graphs;
        };
    })(PrivateForArtifactModule = BbplGraph.PrivateForArtifactModule || (BbplGraph.PrivateForArtifactModule = {}));
    // first part of assignment borrower graph ending in `receivableResponseTapePreview`
    BbplGraph.isPipelineForManualAssignmentPreviewGeneration = (graph, loanProductSlugs) => graph.artifacts.some(({ type }) => type === 'receivableResponseTapePreview') &&
        graph.artifacts.some(({ loanProductSlug }) => loanProductSlug && loanProductSlugs.includes(loanProductSlug));
    // second part of an assignment borrower graph starting with `assignedLoanTape`node
    BbplGraph.isPipelineForManualAssignmentConfirmation = (graph, loanProductSlugs) => graph.artifacts.some(({ type }) => type === 'assignedLoanTape') &&
        graph.artifacts.some(({ loanProductSlug }) => loanProductSlug && loanProductSlugs.includes(loanProductSlug));
    /**
     * Finds the artifact that we should use as the source of truth for cash balance feed fileVersion
     * for the given credit facility.
     */
    BbplGraph.getArtifactForCashBalancesVersion = (graph, creditFacilitySlug) => {
        // We use 'cashBalancesFeedContext' for file version info because an instance of it exists and
        // is updated regardless of where the cash balance information originated (manual uploader,
        // plaid/teller, s3 file).
        const cashBalanceFeedContexts = graph.artifacts.filter(({ type, creditFacilitySlug: artifactCfSlug }) => type === 'cashBalancesFeedContext' &&
            artifactCfSlug === creditFacilitySlug);
        (0, common_1.finleyAssert)(cashBalanceFeedContexts.length === 1, `Expect exactly 1 cashBalancesFeedContext for [creditFacilitySlug: ${creditFacilitySlug}]`);
        return cashBalanceFeedContexts[0];
    };
    BbplGraph.traverseBreadthFirst = (graph, artifactIds, successorFn) => {
        const visitedArtifacts = new Set();
        const idsToVisit = [...artifactIds];
        // eslint-disable-next-line no-restricted-syntax
        while (idsToVisit.length) {
            const currentId = idsToVisit.shift();
            const currentArtifact = graph.artifacts.find(({ id }) => id === currentId);
            (0, common_1.finleyAssert)(currentArtifact, `[artifactId: ${currentId}] not found`);
            if (visitedArtifacts.has(currentArtifact)) {
                continue;
            }
            visitedArtifacts.add(currentArtifact);
            idsToVisit.push(...successorFn(graph, currentId));
        }
        return Array.from(visitedArtifacts); // Set uses insertion order, which gives breadth-first behavior.
    };
    // returns inputs to the given artifactId
    BbplGraph.getDependencies = (graph, artifactId) => graph.dependencies
        .filter(({ outputArtifactId }) => outputArtifactId === artifactId)
        .map(({ inputArtifactId }) => inputArtifactId);
    // returns outputs that depend on the given artifactId
    BbplGraph.getDependants = (graph, artifactId) => graph.dependencies
        .filter(({ inputArtifactId }) => inputArtifactId === artifactId)
        .map(({ outputArtifactId }) => outputArtifactId);
    /**
     * Returns artifacts that the given artifactIds depend on directly or indirectly.
     * Traversal order is breadth-first. Result contains the original artifactIds.
     */
    BbplGraph.findDependenciesRecursively = (graph, artifactIds) => BbplGraph.traverseBreadthFirst(graph, artifactIds, BbplGraph.getDependencies);
    BbplGraph.getGeneratedLoanTapeArtifact = ({ artifacts, loanProductSlug, creditFacilitySlug, }) => {
        const loanTapeArtifacts = artifacts.filter((a) => a.loanProductSlug === loanProductSlug && a.type === 'generatedLoanTape');
        (0, common_1.finleyAssert)(loanTapeArtifacts.length !== 0, `No generatedLoanTape artifacts found for [loanProductSlug:${loanProductSlug}]`);
        // there could be multiple generateLoanTape for same loanProductSlug
        // but with different creditFacilitySlug. if we got more than 1 generated
        // loan-tapes, find the one with matching creditFacilitySlug.
        const loanTapeArtifactsForCF = loanTapeArtifacts.length === 1
            ? loanTapeArtifacts
            : loanTapeArtifacts.filter((artifact) => artifact.creditFacilitySlug === creditFacilitySlug);
        (0, common_1.finleyAssert)(loanTapeArtifactsForCF.length === 1, `generatedLoanTape artifact not found[${loanTapeArtifactsForCF.length}] for [loanProductSlug:${loanProductSlug}], [creditFacilitySlug:${creditFacilitySlug}]`);
        return loanTapeArtifactsForCF[0];
    };
    BbplGraph.getExternalFilenames = (graph) => graph.artifacts
        .map(({ externalFilename }) => externalFilename)
        .filter(common_1.typeguards.isNotNullOrUndefined);
})(BbplGraph || (exports.BbplGraph = BbplGraph = {}));
//# sourceMappingURL=index.js.map